import { useEffect, useRef, useState } from "react";
import { ArrowUp, Square } from "lucide-react";
import type { AskUserQuestionAnswer, OnboardingMessage } from "../../../api/types.js";
import { AskUserQuestionCard } from "../../chat/AskUserQuestionCard.js";

function MessageRow({
  message,
  running,
  onSubmitAsk,
  onStop
}: {
  message: OnboardingMessage;
  running: boolean;
  onSubmitAsk: (messageId: string, answers: AskUserQuestionAnswer[]) => void;
  onStop: () => void;
}) {
  if (message.role === "user") {
    return <article className="onboarding-message onboarding-message-user"><div>{message.content}</div></article>;
  }
  const ask = message.meta?.askUserQuestion;
  const status = message.meta?.askUserQuestionStatus || "pending";
  const isFinalStep = message.meta?.onboardingTopic === "ai_roles"
    || (!message.meta?.onboardingTopic && ask?.submitLabel === "完成");
  return (
    <article className="onboarding-message onboarding-message-assistant">
      {message.content && <p>{message.content}</p>}
      {ask && (
        <AskUserQuestionCard
          ask={ask}
          status={status}
          answers={message.meta?.askUserQuestionAnswers || []}
          disabled={running}
          isFinalStep={isFinalStep}
          onSubmit={(answers) => onSubmitAsk(message.id, answers)}
          onCancel={onStop}
        />
      )}
    </article>
  );
}

function hasPendingAsk(message: OnboardingMessage | null | undefined) {
  return Boolean(
    message
    && message.role === "assistant"
    && message.meta?.askUserQuestion
    && (message.meta.askUserQuestionStatus || "pending") === "pending"
  );
}

export function OnboardingThread({
  messages,
  liveMessage,
  running,
  completed,
  onEnterWorkspace,
  onSubmitAsk,
  onContinueWithText,
  onStop
}: {
  messages: OnboardingMessage[];
  liveMessage: OnboardingMessage | null;
  running: boolean;
  completed: boolean;
  onEnterWorkspace: () => void;
  onSubmitAsk: (messageId: string, answers: AskUserQuestionAnswer[]) => void;
  onContinueWithText: (message: string) => void;
  onStop: () => void;
}) {
  const [text, setText] = useState("");
  // 底部只保留一个入口:运行中是“停止”,空闲且无待回答问题时才是 composer;
  // 结构化问题待回答时回答只走选项或“其他”,防止绕过卡片后 pending ask 永久挂起。
  const askPending = hasPendingAsk(liveMessage) || hasPendingAsk(messages[messages.length - 1]);
  const threadRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  // 中文 IME 组合态守卫,与 Composer.tsx 的 Enter 发送处理一致。
  const composingRef = useRef(false);

  // 新消息与流式增量到达时跟随到底部;用户上滚离开底部后暂停跟随。
  useEffect(() => {
    const node = threadRef.current;
    if (node && pinnedRef.current) node.scrollTop = node.scrollHeight;
  }, [messages, liveMessage, running]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim() || running) return;
    onContinueWithText(text);
    setText("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    const native = event.nativeEvent;
    if (composingRef.current || native.isComposing || native.keyCode === 229) return;
    submit(event);
  };

  return (
    <section className="onboarding-thread-wrap">
      <div
        ref={threadRef}
        className="onboarding-thread"
        data-testid="onboarding-thread"
        aria-live="polite"
        onScroll={() => {
          const node = threadRef.current;
          if (node) pinnedRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
        }}
      >
        <div className="onboarding-thread-column">
          {messages.map((message) => (
            <MessageRow key={message.id} message={message} running={running} onSubmitAsk={onSubmitAsk} onStop={onStop} />
          ))}
          {liveMessage && <MessageRow message={liveMessage} running={running} onSubmitAsk={onSubmitAsk} onStop={onStop} />}
          {running && !liveMessage && <p className="onboarding-running"><span aria-hidden="true" />正在整理你的偏好…</p>}
        </div>
      </div>
      {running ? (
        <div className="onboarding-runbar">
          <button type="button" className="onboarding-stop" onClick={onStop}><Square size={13} />停止</button>
        </div>
      ) : completed ? (
        <div className="onboarding-donebar" data-testid="onboarding-donebar">
          <p>你的偏好档案已建立，之后的回答会参考这些偏好。</p>
          <button type="button" className="onboarding-primary" onClick={onEnterWorkspace}>进入工作台</button>
        </div>
      ) : !askPending && (
        <form className="onboarding-composer" data-testid="onboarding-composer" onSubmit={submit}>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="补充你的回答…"
            aria-label="补充回答"
            rows={1}
            onCompositionStart={() => { composingRef.current = true; }}
            onCompositionEnd={() => { composingRef.current = false; }}
            onKeyDown={handleKeyDown}
          />
          <div className="onboarding-composer-toolbar">
            <button type="submit" className={`onboarding-send${text.trim() ? "" : " is-empty"}`} aria-label="发送" disabled={!text.trim()}><ArrowUp size={18} /></button>
          </div>
        </form>
      )}
    </section>
  );
}
